import { createTheme, Theme } from '@mui/material/styles';

// --- Control: "Matrix" / hacker terminal aesthetic (unchanged) --------------
// green-on-black, monospace, subtle glow.
const MATRIX_GREEN = '#00ff41';
const MATRIX_GREEN_DIM = '#00b32d';
const MATRIX_TEXT = '#b7ffc4';
const PANEL_BG = 'rgba(3, 12, 6, 0.82)';
const PANEL_BORDER = 'rgba(0, 255, 65, 0.28)';

const mono = "'Share Tech Mono', 'Roboto Mono', 'Courier New', monospace";

const matrixTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: MATRIX_GREEN, dark: MATRIX_GREEN_DIM, contrastText: '#001b08' },
    secondary: { main: '#39ff14' },
    success: { main: MATRIX_GREEN },
    warning: { main: '#aaff66' },
    error: { main: '#ff5f56' },
    background: { default: '#000400', paper: PANEL_BG },
    text: { primary: MATRIX_TEXT, secondary: 'rgba(0, 255, 65, 0.65)' },
    divider: PANEL_BORDER,
  },
  typography: {
    fontFamily: mono,
    h1: { fontFamily: mono, letterSpacing: 2 },
    h2: { fontFamily: mono, letterSpacing: 2 },
    h3: { fontFamily: mono, letterSpacing: 3, fontWeight: 400 },
    h4: { fontFamily: mono, letterSpacing: 2 },
    h5: { fontFamily: mono, letterSpacing: 1 },
    h6: { fontFamily: mono, letterSpacing: 1 },
    button: { fontFamily: mono, letterSpacing: 1, textTransform: 'uppercase' },
    subtitle1: { fontFamily: mono, letterSpacing: 1 },
  },
  shape: { borderRadius: 2 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        'html, body, #root': { backgroundColor: '#000400', minHeight: '100%' },
        '::selection': { background: MATRIX_GREEN, color: '#000' },
        a: { color: MATRIX_GREEN, textShadow: '0 0 6px rgba(0,255,65,0.6)' },
        '*::-webkit-scrollbar': { width: 10, height: 10 },
        '*::-webkit-scrollbar-thumb': { background: 'rgba(0,255,65,0.35)', borderRadius: 6 },
        '*::-webkit-scrollbar-track': { background: 'rgba(0,0,0,0.5)' },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: PANEL_BG,
          border: `1px solid ${PANEL_BORDER}`,
          backgroundImage: 'none',
          backdropFilter: 'blur(2px)',
          boxShadow: '0 0 20px rgba(0,255,65,0.08), inset 0 0 24px rgba(0,255,65,0.04)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: PANEL_BG,
          border: `1px solid ${PANEL_BORDER}`,
          backgroundImage: 'none',
          backdropFilter: 'blur(2px)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 2, transition: 'all 0.15s ease' },
        outlined: {
          borderColor: PANEL_BORDER,
          '&:hover': {
            borderColor: MATRIX_GREEN,
            boxShadow: '0 0 12px rgba(0,255,65,0.5)',
            background: 'rgba(0,255,65,0.06)',
          },
        },
        contained: {
          boxShadow: '0 0 14px rgba(0,255,65,0.35)',
          '&:hover': { boxShadow: '0 0 22px rgba(0,255,65,0.7)' },
        },
      },
    },
    MuiChip: { styleOverrides: { root: { fontFamily: mono, borderColor: PANEL_BORDER } } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          fontFamily: mono,
          backgroundColor: 'rgba(0,0,0,0.45)',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: PANEL_BORDER },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,255,65,0.5)' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: MATRIX_GREEN,
            boxShadow: '0 0 10px rgba(0,255,65,0.4)',
          },
        },
        input: { caretColor: MATRIX_GREEN },
      },
    },
    MuiTab: { styleOverrides: { root: { fontFamily: mono, letterSpacing: 1, textTransform: 'uppercase' } } },
    MuiAlert: { styleOverrides: { root: { fontFamily: mono, border: `1px solid ${PANEL_BORDER}` } } },
    MuiTableCell: { styleOverrides: { root: { fontFamily: mono } } },
  },
});

// --- Challenger themes (A/B test) -------------------------------------------
// A small factory so the three non-matrix variants share structure. These use a
// system sans-serif (no extra font load), no green glow, and a softer card look.
const SANS =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

interface ThemeConfig {
  mode: 'light' | 'dark';
  bg: string;
  paper: string;
  paperBorder: string;
  primary: string;
  primaryContrast: string;
  secondary: string;
  textPrimary: string;
  textSecondary: string;
  inputBg: string;
  radius: number;
}

function makeTheme(c: ThemeConfig): Theme {
  const cardShadow =
    c.mode === 'light'
      ? '0 1px 3px rgba(16,24,40,0.08), 0 1px 2px rgba(16,24,40,0.06)'
      : '0 2px 10px rgba(0,0,0,0.45)';
  return createTheme({
    palette: {
      mode: c.mode,
      primary: { main: c.primary, contrastText: c.primaryContrast },
      secondary: { main: c.secondary },
      background: { default: c.bg, paper: c.paper },
      text: { primary: c.textPrimary, secondary: c.textSecondary },
      divider: c.paperBorder,
    },
    typography: {
      fontFamily: SANS,
      button: { textTransform: 'none', fontWeight: 600 },
      h1: { fontFamily: SANS, fontWeight: 700 },
      h2: { fontFamily: SANS, fontWeight: 700 },
      h3: { fontFamily: SANS, fontWeight: 700 },
      h4: { fontFamily: SANS, fontWeight: 700 },
      h5: { fontFamily: SANS, fontWeight: 600 },
      h6: { fontFamily: SANS, fontWeight: 600 },
    },
    shape: { borderRadius: c.radius },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          'html, body, #root': { backgroundColor: c.bg, minHeight: '100%' },
          body: { color: c.textPrimary, fontFamily: SANS },
          '::selection': { background: c.primary, color: c.primaryContrast },
          a: { color: c.primary },
          '*::-webkit-scrollbar': { width: 10, height: 10 },
          '*::-webkit-scrollbar-thumb': { background: c.paperBorder, borderRadius: 6 },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: c.paper,
            border: `1px solid ${c.paperBorder}`,
            backgroundImage: 'none',
            boxShadow: cardShadow,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: c.paper,
            border: `1px solid ${c.paperBorder}`,
            backgroundImage: 'none',
            boxShadow: cardShadow,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: c.radius, transition: 'all 0.15s ease' },
          contained: { boxShadow: 'none', '&:hover': { boxShadow: cardShadow } },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: c.inputBg,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: c.paperBorder },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: c.primary },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: c.primary },
          },
        },
      },
      MuiTab: { styleOverrides: { root: { textTransform: 'none', fontWeight: 600 } } },
    },
  });
}

const cleanTheme = makeTheme({
  mode: 'light',
  bg: '#f7f9fc',
  paper: '#ffffff',
  paperBorder: '#e2e8f0',
  primary: '#2563eb',
  primaryContrast: '#ffffff',
  secondary: '#1a2b4a',
  textPrimary: '#1a2330',
  textSecondary: '#5b6677',
  inputBg: '#ffffff',
  radius: 8,
});

const corporateTheme = makeTheme({
  mode: 'dark',
  bg: '#0f172a',
  paper: '#111c33',
  paperBorder: 'rgba(148,163,184,0.22)',
  primary: '#38bdf8',
  primaryContrast: '#06121f',
  secondary: '#0ea5e9',
  textPrimary: '#e2e8f0',
  textSecondary: '#94a3b8',
  inputBg: 'rgba(2,6,23,0.5)',
  radius: 8,
});

const warmTheme = makeTheme({
  mode: 'light',
  bg: '#fffaf5',
  paper: '#ffffff',
  paperBorder: '#f0e3d8',
  primary: '#ff6b4a',
  primaryContrast: '#ffffff',
  secondary: '#e0892f',
  textPrimary: '#2b2118',
  textSecondary: '#7a6a5c',
  inputBg: '#ffffff',
  radius: 14,
});

// Variant registry. `matrix` is the control; order is irrelevant to assignment.
export const THEMES: Record<string, Theme> = {
  matrix: matrixTheme,
  clean: cleanTheme,
  corporate: corporateTheme,
  warm: warmTheme,
};

export const THEME_VARIANTS = Object.keys(THEMES);

export function getTheme(variant: string): Theme {
  return THEMES[variant] || matrixTheme;
}

export default matrixTheme;
